import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import PacienteModal from '@/components/PacienteModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 120;

// Função para formatar telefone: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
const formatPhone = (value) => {
  if (!value) return '';
  
  // Remove tudo que não é número
  const digits = value.replace(/\D/g, '');
  
  // Remove código do país 55 se presente
  const cleanDigits = digits.startsWith('55') && digits.length >= 12 
    ? digits.slice(2) 
    : digits;
  
  // Aplica a máscara conforme a quantidade de dígitos
  if (cleanDigits.length <= 2) {
    return cleanDigits.length > 0 ? `(${cleanDigits}` : '';
  } else if (cleanDigits.length <= 6) {
    return `(${cleanDigits.slice(0, 2)}) ${cleanDigits.slice(2)}`;
  } else if (cleanDigits.length <= 10) {
    return `(${cleanDigits.slice(0, 2)}) ${cleanDigits.slice(2, 6)}-${cleanDigits.slice(6)}`;
  } else {
    // 11 dígitos (celular)
    return `(${cleanDigits.slice(0, 2)}) ${cleanDigits.slice(2, 7)}-${cleanDigits.slice(7, 11)}`;
  }
};

// Função para formatar CPF: XXX.XXX.XXX-XX
const formatCPF = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

function PacientesPage({ user, onLogout, showConfig }) {
  const navigate = useNavigate();
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [buscaAtual, setBuscaAtual] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedPaciente, setSelectedPaciente] = useState(null);
  const [editingPaciente, setEditingPaciente] = useState(null);
  const [formData, setFormData] = useState({
    nome_completo: '',
    cpf: '',
    data_nascimento: '',
    telefone: '',
    email: '',
    convenio: 'particular',
    observacoes: ''
  });

  useEffect(() => {
    loadPacientes();
  }, []);

  const loadPacientes = async (busca = '', append = false) => {
    const termo = busca.trim();
    const skip = append ? pacientes.length : 0;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${API}/pacientes/resumo`, {
        params: termo
          ? { busca: termo, limit: PAGE_SIZE, skip }
          : { limit: PAGE_SIZE, skip }
      });
      const novosPacientes = response.data || [];
      setPacientes(prev => append ? [...prev, ...novosPacientes] : novosPacientes);
      setHasMore(novosPacientes.length === PAGE_SIZE);
      setBuscaAtual(termo);
    } catch (error) {
      console.error('Erro ao carregar pacientes:', error);
      toast.error('Erro ao carregar pacientes');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadPacientes(searchTerm, false);
  };

  const handleLoadMore = () => {
    loadPacientes(buscaAtual, true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Aplica máscara de telefone
    if (name === 'telefone') {
      setFormData({
        ...formData,
        telefone: formatPhone(value)
      });
    } else if (name === 'cpf') {
      setFormData({
        ...formData,
        cpf: formatCPF(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPaciente) {
        await axios.put(`${API}/pacientes/${editingPaciente.id}`, formData);
        toast.success('Paciente atualizado com sucesso!');
      } else {
        await axios.post(`${API}/pacientes`, formData);
        toast.success('Paciente cadastrado com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      loadPacientes();
    } catch (error) {
      console.error('Erro ao salvar paciente:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar paciente');
    }
  };

  const fetchPacienteCompleto = async (pacienteId) => {
    const response = await axios.get(`${API}/pacientes/${pacienteId}/basico`);
    return response.data;
  };

  const handleViewDetails = async (paciente) => {
    try {
      setDetailsModalOpen(true);
      setSelectedPaciente(paciente);
      const completo = await fetchPacienteCompleto(paciente.id);
      setSelectedPaciente(completo);
    } catch (error) {
      console.error('Erro ao carregar detalhes do paciente:', error);
      toast.error('Erro ao carregar detalhes do paciente');
    }
  };

  const handleEdit = async (paciente) => {
    try {
      const pacienteCompleto = await fetchPacienteCompleto(paciente.id);
      setEditingPaciente(pacienteCompleto);
      setFormData({
        nome_completo: pacienteCompleto.nome_completo || '',
        cpf: formatCPF(pacienteCompleto.cpf || ''),
        data_nascimento: pacienteCompleto.data_nascimento || '',
        telefone: formatPhone(pacienteCompleto.telefone || ''),
        email: pacienteCompleto.email || '',
        convenio: pacienteCompleto.convenio || 'particular',
        observacoes: pacienteCompleto.observacoes || ''
      });
      setDialogOpen(true);
    } catch (error) {
      console.error('Erro ao carregar paciente para edição:', error);
      toast.error('Erro ao carregar dados completos do paciente');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja remover este paciente?')) {
      try {
        await axios.delete(`${API}/pacientes/${id}`);
        toast.success('Paciente removido com sucesso!');
        loadPacientes();
      } catch (error) {
        console.error('Erro ao remover paciente:', error);
        toast.error('Erro ao remover paciente');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nome_completo: '',
      cpf: '',
      data_nascimento: '',
      telefone: '',
      email: '',
      convenio: 'particular',
      observacoes: ''
    });
    setEditingPaciente(null);
  };

  const handleSavePacienteDetails = async (pacienteId, updatedData) => {
    try {
      await axios.put(`${API}/pacientes/${pacienteId}`, updatedData);
      toast.success('Paciente atualizado com sucesso!');
      setDetailsModalOpen(false);
      loadPacientes();
    } catch (error) {
      console.error('Erro ao atualizar paciente:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar paciente');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Parse date as YYYY-MM-DD and format as DD/MM/YYYY
    const [year, month, day] = dateStr.split('-');
    if (!year || !month || !day) return dateStr;
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
  };

  return (
    <Layout user={user} onLogout={onLogout} showConfig={showConfig}>
      <div className="space-y-6" data-testid="pacientes-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Pacientes</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Gerencie os pacientes da clínica</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="w-full sm:w-auto" data-testid="btn-novo-paciente">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Novo Paciente
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPaciente ? 'Editar Paciente' : 'Novo Paciente'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="nome_completo">Nome Completo *</Label>
                    <Input
                      id="nome_completo"
                      name="nome_completo"
                      value={formData.nome_completo}
                      onChange={handleChange}
                      required
                      data-testid="input-nome-completo"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cpf">CPF</Label>
                    <Input
                      id="cpf"
                      name="cpf"
                      value={formData.cpf}
                      onChange={handleChange}
                      placeholder="000.000.000-00"
                      data-testid="input-cpf"
                    />
                  </div>
                  <div>
                    <Label htmlFor="data_nascimento">Data de Nascimento</Label>
                    <Input
                      id="data_nascimento"
                      name="data_nascimento"
                      type="date"
                      value={formData.data_nascimento}
                      onChange={handleChange}
                      data-testid="input-data-nascimento"
                    />
                  </div>
                  <div>
                    <Label htmlFor="telefone">Telefone</Label>
                    <Input
                      id="telefone"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleChange}
                      placeholder="(11) 98765-4321"
                      maxLength={16}
                      data-testid="input-telefone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">E-mail</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      data-testid="input-email-paciente"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="convenio">Convênio</Label>
                    <select
                      id="convenio"
                      name="convenio"
                      value={formData.convenio}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="select-convenio"
                    >
                      <option value="particular">Particular</option>
                      <option value="unimed">Unimed</option>
                      <option value="amil">Amil</option>
                      <option value="bradesco">Bradesco Saúde</option>
                      <option value="sulamerica">SulAmérica</option>
                      <option value="outro">Outro</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="observacoes">Observações</Label>
                    <textarea
                      id="observacoes"
                      name="observacoes"
                      value={formData.observacoes}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="textarea-observacoes"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                    data-testid="btn-cancelar"
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" data-testid="btn-salvar-paciente">
                    {editingPaciente ? 'Atualizar' : 'Cadastrar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <Card className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <Input
              type="text"
              placeholder="Buscar por nome, CPF ou telefone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
              data-testid="input-busca"
            />
            <div className="flex gap-3">
              <Button type="submit" className="flex-1 sm:flex-initial" data-testid="btn-buscar">
                <svg className="w-5 h-5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="hidden sm:inline">Buscar</span>
              </Button>
              {searchTerm && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 sm:flex-initial"
                  onClick={() => { setSearchTerm(''); loadPacientes('', false); }}
                  data-testid="btn-limpar-busca"
                >
                  Limpar
                </Button>
              )}
            </div>
          </form>
        </Card>

        {/* Table/Cards */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : pacientes.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="mt-4 text-gray-600">Nenhum paciente encontrado</p>
            </div>
          ) : (
            <>
              {/* Desktop Table - Hidden on mobile */}
              <div className="hidden md:block overflow-x-auto">
                <table className="data-table" data-testid="table-pacientes">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>CPF</th>
                      <th>Telefone</th>
                      <th>Convênio</th>
                      <th>Data Nasc.</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pacientes.map((paciente) => (
                      <tr 
                        key={paciente.id} 
                        data-testid={`paciente-row-${paciente.id}`}
                        className="cursor-pointer hover:bg-gray-50 border-l-[6px] border-l-transparent transition-colors"
                        onClick={() => handleViewDetails(paciente)}
                      >
                        <td className="font-medium">{paciente.nome_completo}</td>
                        <td>{formatCPF(paciente.cpf) || '-'}</td>
                        <td>{paciente.telefone || '-'}</td>
                        <td>
                          <span className="badge badge-blue capitalize">
                            {paciente.convenio || 'particular'}
                          </span>
                        </td>
                        <td>{formatDate(paciente.data_nascimento)}</td>
                        <td>
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                navigate(`/pacientes/prontuario/${paciente.id}`);
                              }}
                              className="px-3 py-1 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded"
                              data-testid={`btn-prontuario-${paciente.id}`}
                            >
                              Prontuário
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEdit(paciente); }}
                              className="px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
                              data-testid={`btn-edit-${paciente.id}`}
                            >
                              Editar
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDelete(paciente.id); }}
                              className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded"
                              data-testid={`btn-delete-${paciente.id}`}
                            >
                              Excluir
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards - Shown only on mobile */}
              <div className="md:hidden divide-y divide-gray-200">
                {pacientes.map((paciente) => (
                  <div
                    key={paciente.id}
                    className="p-4 hover:bg-gray-50 border-l-[6px] border-l-transparent transition-colors"
                    data-testid={`paciente-card-${paciente.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0" onClick={() => handleViewDetails(paciente)}>
                        <h3 className="text-base font-semibold text-gray-900 truncate">
                          {paciente.nome_completo}
                        </h3>
                        <span className="inline-block mt-1 badge badge-blue capitalize text-xs">
                          {paciente.convenio || 'particular'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm mb-3">
                      {paciente.cpf && (
                        <div className="flex items-center text-gray-600">
                          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                          </svg>
                          <span className="truncate">CPF: {formatCPF(paciente.cpf)}</span>
                        </div>
                      )}
                      {paciente.telefone && (
                        <div className="flex items-center text-gray-600">
                          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span className="truncate">{paciente.telefone}</span>
                        </div>
                      )}
                      {paciente.data_nascimento && (
                        <div className="flex items-center text-gray-600">
                          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>Nascimento: {formatDate(paciente.data_nascimento)}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Action Buttons Mobile */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => navigate(`/pacientes/prontuario/${paciente.id}`)}
                        className="flex-1 px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded"
                        data-testid={`btn-prontuario-mobile-${paciente.id}`}
                      >
                        Prontuário
                      </button>
                      <button
                        onClick={() => handleEdit(paciente)}
                        className="flex-1 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
                        data-testid={`btn-edit-mobile-${paciente.id}`}
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(paciente.id)}
                        className="flex-1 px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded"
                        data-testid={`btn-delete-mobile-${paciente.id}`}
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {hasMore && (
                <div className="border-t border-gray-100 p-4 flex justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    data-testid="btn-carregar-mais-pacientes"
                  >
                    {loadingMore ? 'Carregando...' : 'Carregar mais pacientes'}
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
      
      {/* Modal de Detalhes */}
      <PacienteModal 
        paciente={selectedPaciente}
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        onSave={handleSavePacienteDetails}
      />
    </Layout>
  );
}

export default PacientesPage;
